import React, { useEffect, useState } from "react";
import {
  getExceptionDetails,
  connectPromotedRecords,
  PreviewManualMatching,
  displayAttributesTooltip,
  GetSFMDisplay,
} from "@helpers/helpers";
import { useLocation } from "react-router-dom";
import Loader from "@components/LoaderComponent/loader";
import TooltipComponent from "@components/TooltipComponent";
import SfmModalStore from "@stores/SfmModalStore";
import { useTranslation } from "react-i18next";
import { ZERO_VALUE } from "@utils/constants";
import NoDataComponent from "@helpers/NoDataComponent";

import TableHeader from "./TableHeader";
import MainTable from "./MainTable";
import OutstandingRecords from "../OutstandingRecords";
import PopupComponent from "@components/PopupComponent";
import CorrectiveActionComponent from "@components/CorrectiveActionComponent";
import { createMainColumns, createChildrenColumns } from "./columnDefinitions";

const ExceptionDetails = ({ resetComponent }) => {
  const { t: translate, i18n } = useTranslation();
  const location = useLocation();

  const [exceptionDetailsMatchingRecord, setExcpetionDetailMatchingRecord] =
    useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);
  const [attributesPopupOpen, setAttributesPopupOpen] = useState(false);
  const [popupLoading, setPopupLoading] = useState(false);
  const [correctiveAction, setCorrectiveAction] = useState(null);
  const [selectedRows, setSelectedRows] = useState({});
  const [currentTooltip, setCurrentTooltip] = useState(null);
  const [externalOutstandingRecords, setExternalOutstandingRecords] = useState(
    []
  );
  const [
    selectedOutstandingDestinationRows,
    setSelectedOutstandingDestinationRows,
  ] = useState({});

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get("id");

    getExceptionDetails(id, (exceptionDetailsInfo) => {
      let matchingRecord = [];
      if (
        exceptionDetailsInfo?.PromotedRecords ||
        exceptionDetailsInfo?.MatchingRecord
      ) {
        matchingRecord = connectPromotedRecords(
          exceptionDetailsInfo?.PromotedRecords,
          [exceptionDetailsInfo?.MatchingRecord]
        );
      } else {
        const sfmData = GetSFMDisplay(exceptionDetailsInfo.SFM);
        SfmModalStore.open(sfmData, () => {
          window.location.href = "/exception-list";
        });
      }
      setCurrentId(id);
      setExcpetionDetailMatchingRecord(matchingRecord);
      setIsLoading(false);
    });
  }, [location]);

  const columns = createMainColumns(translate);
  const childrenColumns = createChildrenColumns(
    translate,
    setAttributesPopupOpen,
    setCurrentTooltip
  );

  const prepareTableData = (exceptionDetailsMatchingRecord) => {
    const childrenRecords = [];
    const matchingRecords = [];

    exceptionDetailsMatchingRecord?.forEach((record) => {
      matchingRecords.push(record);
      if (record.SourceRecords.length) {
        record.SourceRecords.forEach((sourceRecord) => {
          childrenRecords.push({
            parentRecordID: record.MatchingRecordId,
            ...sourceRecord,
            source: "Source",
          });
        });
      }
      if (record.DestinationRecords.length) {
        record.DestinationRecords.forEach((destinationRecord) => {
          childrenRecords.push({
            parentRecordID: record.MatchingRecordId,
            ...destinationRecord,
            source: "Destination",
          });
        });
      }
    });

    return { matchingRecords, childrenRecords };
  };

  const handleRefresh = () => {
    resetComponent();
  };

  const handleCorrectiveResults = () => {
    const combinedExceptionsDetailsKeys = Object.keys(selectedRows);
    const combinedOutstandingRecordsKeys = Object.keys(
      selectedOutstandingDestinationRows
    );

    setPopupLoading(true);
    PreviewManualMatching(
      externalOutstandingRecords,
      exceptionDetailsMatchingRecord,
      combinedExceptionsDetailsKeys,
      combinedOutstandingRecordsKeys,
      (correctiveAction) => {
        if (!correctiveAction.CorrectionResults) {
          const sfmData = GetSFMDisplay(correctiveAction.SFM);
          SfmModalStore.open(sfmData);
          setPopupLoading(false);
        } else {
          setCorrectiveAction(correctiveAction);
          setPopupLoading(false);
          setPopupOpen(true);
        }
      }
    );
  };

  const isCorrectionDisabled =
    Object.keys(selectedRows).length === ZERO_VALUE ||
    Object.keys(selectedOutstandingDestinationRows).length === ZERO_VALUE;

  if (isLoading) {
    return <Loader dots withContainer />;
  }

  const { matchingRecords, childrenRecords } = prepareTableData(
    exceptionDetailsMatchingRecord
  );

  return (
    <div>
      <div className="border-2 border-gray-200 rounded mr-5 text-black dark:border-gray-700">
        <TableHeader
          onRefresh={handleRefresh}
          onCorrectiveResults={handleCorrectiveResults}
          isCorrectionDisabled={isCorrectionDisabled}
          isLoading={popupLoading}
        />
        <MainTable
          columns={columns}
          childrenColumns={childrenColumns}
          matchingRecords={matchingRecords}
          childrenRecords={childrenRecords}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          attributesPopupOpen={attributesPopupOpen}
          setAttributesPopupOpen={setAttributesPopupOpen}
          setCurrentTooltip={setCurrentTooltip}
          noData={<NoDataComponent />}
        />
        <TooltipComponent
          anchorElement={"attributes-tooltip"}
          toolTipMessage={displayAttributesTooltip(currentTooltip)}
          position="top"
          className="max-w-max z-50 !bg-gray-500"
        />
      </div>
      <OutstandingRecords
        exceptionDetailsMatchingRecord={exceptionDetailsMatchingRecord}
        selectedOutstandingDestinationRows={selectedOutstandingDestinationRows}
        setSelectedOutstandingDestinationRows={
          setSelectedOutstandingDestinationRows
        }
        setExternalOutstandingRecords={setExternalOutstandingRecords}
        key={`outstandingRecords-${externalOutstandingRecords?.data?.length}`}
        recordId={currentId}
      />
      <PopupComponent
        open={popupOpen}
        rawValue={
          <CorrectiveActionComponent
            correctiveAction={connectPromotedRecords(
              correctiveAction?.PromotedRecords,
              correctiveAction?.CorrectionResults
            )}
            setPopupOpen={setPopupOpen}
            MatchingRequestId={correctiveAction?.ManualMatchingRequestId}
            resetComponent={resetComponent}
            translate={translate}
          />
        }
        setPopupOpen={setPopupOpen}
        addBlur
        hideCellValue={true}
        popupLoading={popupLoading}
        z="101"
      />
    </div>
  );
};

export default ExceptionDetails;
